import { Popover as PopoverPrimitive } from '@base-ui/react'
import { Columns3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { cn } from '@/lib/cn'

/* Which columns this screen shows (UX-DR-4).
 *
 * The menu OWNS NOTHING. It is handed the column list, the ids that are
 * currently off and two callbacks, because the thing that remembers the choice
 * is `useViewState` and the thing that knows which columns a surface has is the
 * surface. A control that also persisted would be a second writer of the same
 * localStorage entry, and the page and the popover would drift the first time
 * one of them re-rendered without the other.
 *
 * A column may be LOCKED. Every dense list here is read by its identifier —
 * the quote number, the case number — and a list whose identifier column has
 * been turned off is a grid of anonymous rows the user cannot get back from
 * without finding this menu again. Locked columns are still listed, ticked and
 * disabled, so the answer to "where did the number column go" is visible
 * rather than absent.
 *
 * Built on the same Base UI popover the date picker uses, with the same
 * portal-and-positioner arrangement: a menu opened from a toolbar inside a
 * scrollable list is clipped by that list otherwise.
 */

export interface ColumnsMenuColumn {
  /** The TanStack column id. Must match what the table knows it as. */
  id: string
  label: string
  /** Cannot be turned off — see the note above about identifier columns. */
  locked?: boolean
}

export interface ColumnsMenuProps {
  columns: readonly ColumnsMenuColumn[]
  /** Ids currently turned OFF. */
  hidden: readonly string[]
  onToggle: (id: string) => void
  /** Show every column again. */
  onReset: () => void
  className?: string
}

export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
  onReset,
  className,
}: ColumnsMenuProps) {
  const { t } = useTranslation()

  /* Only counts columns that CAN be hidden. A locked id sitting in the stored
     list — a column that used to be optional and no longer is — must not
     inflate a count the user has no way of clearing. */
  const hiddenCount = columns.filter(
    (column) => !column.locked && hidden.includes(column.id),
  ).length

  return (
    <PopoverPrimitive.Root
      /* Not modal, for the reason the date picker gives: this opens from a
         toolbar that may itself be inside a sheet, and a second scroll lock
         over the sheet's own leaves the page unscrollable after it closes. */
      modal={false}
    >
      <PopoverPrimitive.Trigger
        render={<Button variant="secondary" size="sm" iconLeft={Columns3} />}
        className={className}
      >
        {t('common.columns.label')}
        {hiddenCount > 0 ? (
          <>
            {/* The badge is for the eye; the sentence beside it is what a
                screen reader reads, because "3" on its own says nothing. */}
            <span
              aria-hidden
              className="bg-surface-sunken text-text-muted asa-num rounded-full px-1.5 text-[11px]"
            >
              {hiddenCount}
            </span>
            <span className="sr-only">
              {t('common.columns.hiddenCount', { count: hiddenCount })}
            </span>
          </>
        ) : null}
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner sideOffset={4} align="end" className="z-[60]">
          <PopoverPrimitive.Popup
            aria-label={t('common.columns.label')}
            className={cn(
              'bg-surface border-border-subtle rounded-md border',
              'shadow-[var(--shadow-overlay)] w-[248px] p-2',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            {/* The description says THIS SCREEN rather than "your preferences":
                the choice is a workstation habit stored per user per surface,
                not a profile setting, and the copy should not promise more
                reach than the storage has. */}
            <p className="text-text-muted px-2 pt-1 pb-2 text-[11.5px] leading-snug font-light">
              {t('common.columns.description')}
            </p>

            <ul className="asa-scroll flex max-h-[300px] flex-col gap-0.5 overflow-y-auto">
              {columns.map((column) => (
                <li key={column.id}>
                  <Checkbox
                    className="px-2 py-1.5"
                    label={column.label}
                    /* A locked column reads as TICKED whatever the stored list
                       says. An id left over from a release where the column was
                       optional would otherwise render unchecked-and-disabled
                       beside a table that is still showing it — the box
                       contradicting the grid, with no way to press it back into
                       agreement. The table filters locked ids out of visibility
                       for the same reason; this is the control saying so. */
                    checked={column.locked === true || !hidden.includes(column.id)}
                    disabled={column.locked}
                    onCheckedChange={() => onToggle(column.id)}
                  />
                </li>
              ))}
            </ul>

            {/* One press back to the default. `blockedReason` is not used here
                because there is no rule to explain — with nothing hidden the
                action simply has no work to do, and its own label already says
                what it would have done. */}
            <div className="border-line-hair mt-2 border-t pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={hiddenCount === 0}
                onClick={onReset}
              >
                {t('common.columns.reset')}
              </Button>
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
